import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toggleReaction, DEFAULT_META } from '../data/metaApi.js'
import { useMetaData } from './useMetaData.js'
import { useUIStore } from '../store/uiStore.js'

/**
 * useReactions — read + toggle emoji reactions for a given label.
 *
 * Reactions live in meta.reactions[label] as:
 *   { '👍': ['Ricky', 'Zoe'], '🎉': ['Katie'] }
 *
 * Optimistic cache write + server-side authoritative response on success.
 * Follows the same pattern as useActionItemOp to stay safe against
 * full-meta POST clobbers.
 *
 * Usage:
 *   const { reactions, toggle, myEmojis } = useReactions('Hit $200k MRR')
 *   toggle('🔥')
 */
export function useReactions(label) {
  const { data: meta } = useMetaData()
  const queryClient = useQueryClient()
  const currentUser = useUIStore((s) => s.currentUser)

  const reactions = (meta?.reactions?.[label]) || {}

  // Which emojis has the current user reacted with? Used to show a
  // highlighted "you reacted" state in the UI.
  const myEmojis = currentUser
    ? new Set(
        Object.entries(reactions)
          .filter(([, users]) => Array.isArray(users) && users.includes(currentUser))
          .map(([emoji]) => emoji)
      )
    : new Set()

  const mutation = useMutation({
    mutationFn: async (emoji) => {
      if (!currentUser) throw new Error('No current user — can\'t react')
      const next = await toggleReaction({ label, emoji, user: currentUser })
      return next
    },
    onMutate: async (emoji) => {
      if (!currentUser) return
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META
      const current = previous.reactions?.[label] || {}
      const users = Array.isArray(current[emoji]) ? current[emoji] : []
      const has = users.includes(currentUser)
      const nextUsers = has
        ? users.filter((u) => u !== currentUser)
        : [...users, currentUser]
      const nextBucket = { ...current }
      if (nextUsers.length === 0) delete nextBucket[emoji]
      else nextBucket[emoji] = nextUsers

      const nextReactions = { ...(previous.reactions || {}) }
      if (Object.keys(nextBucket).length === 0) delete nextReactions[label]
      else nextReactions[label] = nextBucket

      queryClient.setQueryData(['meta'], {
        ...previous,
        reactions: nextReactions,
      })
      return { previous }
    },
    onSuccess: (nextForLabel) => {
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      const nextReactions = { ...(current.reactions || {}) }
      if (Object.keys(nextForLabel).length === 0) delete nextReactions[label]
      else nextReactions[label] = nextForLabel
      queryClient.setQueryData(['meta'], { ...current, reactions: nextReactions })
    },
    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })

  return {
    reactions,
    myEmojis,
    toggle: (emoji) => mutation.mutate(emoji),
    isPending: mutation.isPending,
  }
}
